import {
  BasketRead,
  ChefGroup,
  FixedMixOverride,
  MaterialConstraintClass,
  MaterialLimitRead,
  MaterialRead,
  NameBasedMaterialChemistry,
  NameBasedMaterialLimit,
  SearchContextData,
} from "store/api/generatedApi";

export const materialLimitKey = (
  chefGroupName: string,
  name: string | null | undefined
): string => (name ? `${chefGroupName} – ${name}` : chefGroupName);

export const materialChemistryKey = (
  materialName: string,
  chemicalElementName: string
): string => `${chemicalElementName}-${materialName}`;

const toNameBasedMaterialLimit = (
  limit: MaterialLimitRead,
  chefGroupName: string,
  basketNumberById: Map<number, number>,
  classNameById: Map<number, string>
): NameBasedMaterialLimit => ({
  chef_group_name: chefGroupName,
  name: limit.name ?? null,
  hardness: limit.hardness,
  coefficients: limit.coefficients
    .map((coefficient) => ({
      material_constraint_class_name:
        classNameById.get(coefficient.material_constraint_class_id) ?? "",
      coefficient: coefficient.coefficient,
    }))
    .filter((coefficient) => coefficient.material_constraint_class_name !== ""),
  bounds: limit.bounds.map((bound) => ({
    basket_number:
      bound.basket_id === null
        ? null
        : (basketNumberById.get(bound.basket_id) ?? null),
    min_mass: bound.min_mass,
    max_mass: bound.max_mass,
    soft_min_mass: bound.soft_min_mass,
    soft_max_mass: bound.soft_max_mass,
  })),
});

export const buildBaseMaterialLimitLookup = (
  context: SearchContextData | undefined,
  classes: MaterialConstraintClass[] | undefined
): Map<string, NameBasedMaterialLimit> => {
  if (!context || !classes) return new Map();
  const chefGroupNameById = new Map<number, string>(
    context.chef_groups
      .filter(
        (chefGroup): chefGroup is ChefGroup & { id: number } =>
          chefGroup.id != null
      )
      .map((chefGroup) => [chefGroup.id, chefGroup.name])
  );
  const basketNumberById = new Map<number, number>(
    context.baskets.map((basket: BasketRead) => [
      basket.id,
      basket.basket_number,
    ])
  );
  const classNameById = new Map<number, string>(
    classes.map((constraintClass) => [constraintClass.id, constraintClass.name])
  );

  const limitByKey = new Map<string, NameBasedMaterialLimit>();
  for (const limit of context.material_limits) {
    const chefGroupName = chefGroupNameById.get(limit.chef_group_id);
    if (chefGroupName === undefined) continue;
    limitByKey.set(
      materialLimitKey(chefGroupName, limit.name),
      toNameBasedMaterialLimit(
        limit,
        chefGroupName,
        basketNumberById,
        classNameById
      )
    );
  }
  return limitByKey;
};

export const buildBaseMaterialChemistryLookup = (
  context: SearchContextData | undefined
): Map<string, NameBasedMaterialChemistry> => {
  if (!context) return new Map();
  const materialNameById = new Map<number, string>(
    context.materials.map((material: MaterialRead) => [
      material.id,
      material.name,
    ])
  );
  const elementNameById = new Map<number, string>(
    context.chemical_elements.map((element) => [element.id, element.name])
  );

  const chemistryByKey = new Map<string, NameBasedMaterialChemistry>();
  for (const chemistry of context.material_chemistry) {
    const materialName = materialNameById.get(chemistry.material_id);
    const elementName = elementNameById.get(chemistry.chemical_element_id);
    if (materialName === undefined || elementName === undefined) continue;
    chemistryByKey.set(materialChemistryKey(materialName, elementName), {
      material_name: materialName,
      chemical_element_name: elementName,
      loc: chemistry.loc,
      scale: chemistry.scale,
    });
  }
  return chemistryByKey;
};

export const buildBaseFixedMixLookup = (
  context: SearchContextData | undefined
): Map<string, FixedMixOverride> => {
  if (!context?.fixed_mixes) return new Map();
  return new Map(
    context.fixed_mixes.map((fixedMix) => [fixedMix.steel_grade_name, fixedMix])
  );
};
